import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  fetchTruck7Listings,
  formatPrice,
  formatRegistration,
  formatMileage,
  getCountryFlag,
} from "../../services/truck7Service";
import Container from "../UI/Container";

const BannerWrapper = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  margin: 3rem 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 1.5rem;
  }
`;

const BannerHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h3 {
    color: #1e293b;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }

  p {
    color: #64748b;
    font-size: 1rem;
    margin: 0;
  }

  .truck7-logo {
    display: inline-flex;
    align-items: center;
    background: #ff6b35;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-weight: 700;
    font-size: 1.1rem;
    text-decoration: none;
    margin-bottom: 1rem;

    &:hover {
      background: #e55a2b;
      color: white;
      text-decoration: none;
    }
  }

  @media (max-width: 768px) {
    h3 {
      font-size: 1.25rem;
    }

    p {
      font-size: 0.9rem;
    }
  }
`;

const ListingsGrid = styled.div`
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 3px;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ListingCard = styled.a`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  min-width: 280px;
  width: 280px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    text-decoration: none;
    color: inherit;
    border-color: #ff6b35;
  }
`;

const ListingImage = styled.div`
  width: 100%;
  height: 158px; /* Maintains 16:9 aspect ratio for 280px width */
  background-image: url(${(props) => props.src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  border-radius: 6px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    height: 180px; /* Adjusted for mobile responsiveness */
  }
`;

const ListingTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ListingMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #64748b;

  .label {
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #374151;
  }
`;

const PriceTag = styled.div`
  background: #ff6b35;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1rem;
  text-align: center;
  margin-top: 0.75rem;
`;

const CountryFlag = styled.span`
  font-size: 1.1rem;
  margin-right: 0.25rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #64748b;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const ViewAllButton = styled.a`
  display: inline-block;
  margin-top: 2rem;
  padding: 0.75rem 2rem;
  background: #ff6b35;
  color: white;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #e55a2b;
    color: white;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const Truck7HorizontalBanner = ({ limit = 5 }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadListings = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await fetchTruck7Listings(limit);
        setListings(data);
      } catch (err) {
        console.error("Error loading Truck7 listings:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, [limit]);

  if (loading) {
    return (
      <Container gutter="medium">
        <BannerWrapper>
          <LoadingState>Loading featured vehicles...</LoadingState>
        </BannerWrapper>
      </Container>
    );
  }

  if (error || !listings || listings.length === 0) {
    return (
      <Container gutter="medium">
        <BannerWrapper>
          <ErrorState>
            <p>Unable to load featured vehicles at the moment.</p>
            <p>
              <a
                href="https://truck7.eu"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#ff6b35", textDecoration: "none" }}
              >
                Visit Truck7.eu →
              </a>
            </p>
          </ErrorState>
        </BannerWrapper>
      </Container>
    );
  }

  return (
    <Container gutter="medium">
      <BannerWrapper>
        <BannerHeader>
          <div>
            <a
              href="https://truck7.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="truck7-logo"
            >
              Truck7
            </a>
          </div>
          <h3>Featured commercial vehicles at Truck7.eu</h3>
          <p>
            Truck7.eu is the fastest-growing international truck and trailer
            marketplace in Europe, trusted by more than 700 dealerships and
            over 8,000 transport companies.
          </p>
        </BannerHeader>

        <ListingsGrid>
          {listings.map((listing) => (
            <ListingCard
              key={listing.id}
              href={listing.public_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListingImage
                src={
                  listing.images && listing.images.length > 0
                    ? listing.images[0]
                    : ""
                }
              />

              <ListingTitle>{listing.listing_title}</ListingTitle>

              <ListingMeta>
                <MetaRow>
                  <span className="label">Location:</span>
                  <span className="value">
                    <CountryFlag>{getCountryFlag(listing.country)}</CountryFlag>
                    {listing.country}
                  </span>
                </MetaRow>

                {listing.attributes?.first_registration_year && (
                  <MetaRow>
                    <span className="label">Registration:</span>
                    <span className="value">
                      {formatRegistration(listing.attributes)}
                    </span>
                  </MetaRow>
                )}

                {listing.attributes?.mileage_km && (
                  <MetaRow>
                    <span className="label">Mileage:</span>
                    <span className="value">
                      {formatMileage(listing.attributes)}
                    </span>
                  </MetaRow>
                )}

                {listing.attributes?.fuel_type && (
                  <MetaRow>
                    <span className="label">Fuel:</span>
                    <span className="value">
                      {listing.attributes.fuel_type}
                    </span>
                  </MetaRow>
                )}
              </ListingMeta>

              <PriceTag>{formatPrice(listing.price)}</PriceTag>
            </ListingCard>
          ))}
        </ListingsGrid>

        <ButtonContainer>
          <ViewAllButton
            href="https://truck7.eu"
            target="_blank"
            rel="noopener noreferrer"
          >
            View All Vehicles →
          </ViewAllButton>
        </ButtonContainer>
      </BannerWrapper>
    </Container>
  );
};

export default Truck7HorizontalBanner;
